import React, { useState, useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import pb from '../../lib/pocketbase';
import styles from '../Account.module.css'; // Reusing existing styles for container
import { FaMapMarkedAlt, FaFilter } from 'react-icons/fa';

// Fix for default Leaflet icons in React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Custom Icons
const createIcon = (color) => new L.Icon({
    iconUrl: `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-${color}.png`,
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
});

const icons = {
    'green': createIcon('green'),
    'red': createIcon('red'),
    'blue': createIcon('blue'),
    'grey': createIcon('grey'),
    'orange': createIcon('orange')
};

const AdminMap = () => {
    const [chantiers, setChantiers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('ALL'); // ALL, EN_COURS, TERMINE, RETARD, OVERBUDGET

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [records, interventions, timesheets] = await Promise.all([
                    pb.collection('chantiers').getFullList({ sort: '-created', expand: 'client' }),
                    pb.collection('interventions').getFullList(),
                    pb.collection('timesheets').getFullList()
                ]);

                const enrichedChantiers = records.map(c => {
                    const chantierInters = interventions.filter(i => i.project_id === c.id);
                    const planned_hours = chantierInters.reduce((acc, i) => acc + (parseFloat(i.hours_override) || 8), 0);
                    
                    const chantierTimesheets = timesheets.filter(t => t.chantier === c.id);
                    const real_hours = chantierTimesheets.reduce((acc, t) => acc + (t.hours || 0), 0);
                    const approved_hours = chantierTimesheets.filter(t => t.status === 'approved').reduce((acc, t) => acc + (t.hours || 0), 0);

                    const unique_workers_real = new Set(chantierTimesheets.map(t => t.worker).filter(Boolean)).size;
                    const unique_workers_planned = new Set(chantierInters.flatMap(i => i.assigned_staff || []).filter(Boolean)).size;

                    const isOverdue = c.end_date_estimated && new Date(c.end_date_estimated) < new Date() && c.status !== 'TERMINE';
                    const budget = parseFloat(c.budget_hours) || 0;
                    const isOverbudget = budget > 0 && real_hours > budget;

                    // Determine Icon color
                    let color = 'grey'; // Default PLANNING
                    if (c.status === 'TERMINE') color = 'blue';
                    else if (c.status === 'EN_COURS') {
                        if (isOverbudget) color = 'red';
                        else if (isOverdue) color = 'orange';
                        else color = 'green';
                    }
                    else if (c.status === 'ANNULE') color = 'red';

                    return { 
                        ...c, 
                        planned_hours, 
                        real_hours, 
                        approved_hours, 
                        unique_workers_real, 
                        unique_workers_planned,
                        isOverdue,
                        isOverbudget,
                        icon: icons[color],
                        displayLat: parseFloat(c.lat) || (46.603354 + (Math.random() * 2 - 1)), // Random spread if missing
                        displayLon: parseFloat(c.lon || c.lng) || (1.888334 + (Math.random() * 2 - 1)) // Random spread if missing
                    };
                });

                setChantiers(enrichedChantiers);
            } catch (err) {
                console.error("Error fetching chantiers for map:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const filteredMarkers = useMemo(() => {
        return chantiers.filter(c => {
            if (filter === 'ALL') return true;
            if (filter === 'EN_COURS') return c.status === 'EN_COURS';
            if (filter === 'TERMINE') return c.status === 'TERMINE';
            if (filter === 'RETARD') return c.isOverdue;
            if (filter === 'OVERBUDGET') return c.isOverbudget;
            return true;
        });
    }, [chantiers, filter]);

    return (
        <div style={{ padding: '20px', height: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
                <div>
                    <h2 className={styles.contentTitle} style={{ marginBottom: '5px' }}>
                        <FaMapMarkedAlt style={{ marginRight: '10px' }} />
                        Cockpit Chantiers
                    </h2>
                    <p style={{ margin: 0, opacity: 0.7 }}>Pilotage géographique et financier en temps réel.</p>
                </div>

                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', background: '#f8fafc', padding: '5px 10px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                    <FaFilter style={{ color: '#64748b' }} />
                    <select 
                        value={filter} 
                        onChange={(e) => setFilter(e.target.value)}
                        style={{ border: 'none', background: 'transparent', fontWeight: 'bold', color: '#1e293b', outline: 'none', cursor: 'pointer' }}
                    >
                        <option value="ALL">Tous les chantiers</option>
                        <option value="EN_COURS">▶ En Cours</option>
                        <option value="OVERBUDGET">🚨 Budget Dépassé</option>
                        <option value="RETARD">⏱ En Retard</option>
                        <option value="TERMINE">✅ Terminés</option>
                    </select>
                </div>

                <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', fontSize: '12px', fontWeight: 'bold' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><span style={{ display: 'block', width: 10, height: 10, borderRadius: '50%', background: '#2aad27' }}></span> En cours (OK)</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><span style={{ display: 'block', width: 10, height: 10, borderRadius: '50%', background: '#cb2b3e' }}></span> Budget dépassé</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><span style={{ display: 'block', width: 10, height: 10, borderRadius: '50%', background: '#cb8427' }}></span> En retard</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><span style={{ display: 'block', width: 10, height: 10, borderRadius: '50%', background: '#2a81cb' }}></span> Terminé</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><span style={{ display: 'block', width: 10, height: 10, borderRadius: '50%', background: '#7b7b7b' }}></span> Planifié</div>
                </div>
            </div>

            <div style={{ flex: 1, borderRadius: '16px', overflow: 'hidden', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', minHeight: '500px', zIndex: 0 }}>
                {loading ? (
                    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
                        <div className={styles.loading}>Chargement ultra-rapide de la carte...</div>
                    </div>
                ) : (
                    <MapContainer center={[46.603354, 1.888334]} zoom={6} scrollWheelZoom={true} style={{ height: '100%', width: '100%' }}>
                        <TileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        <MarkerClusterGroup chunkedLoading>
                            {filteredMarkers.map(c => (
                                <Marker key={c.id} position={[c.displayLat, c.displayLon]} icon={c.icon}>
                                    <Popup>
                                        <div style={{ minWidth: '220px' }}>
                                            <h3 style={{ margin: '0 0 5px', fontSize: '1.1rem', fontWeight: 'bold' }}>{c.name}</h3>
                                            <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '10px' }}>{c.address}</div>
                                            
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                                <span style={{ fontWeight: '600', fontSize: '0.8rem' }}>Client : {c.expand?.client?.name || '-'}</span>
                                                <span style={{ 
                                                    background: c.status === 'TERMINE' ? '#dcfce7' : (c.status === 'EN_COURS' ? '#fef3c7' : '#f1f5f9'), 
                                                    color: c.status === 'TERMINE' ? '#166534' : (c.status === 'EN_COURS' ? '#92400e' : '#475569'), 
                                                    padding: '2px 6px', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 'bold' 
                                                }}>
                                                    {c.status}
                                                </span>
                                            </div>

                                            {c.budget_hours > 0 && (
                                                <div style={{ background: '#f8fafc', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '10px' }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', fontWeight: 'bold', marginBottom: '5px' }}>
                                                        <span>Consommation Heures</span>
                                                        <span>{c.budget_hours}h</span>
                                                    </div>
                                                    <div style={{ height: '6px', background: '#e2e8f0', borderRadius: '3px', overflow: 'hidden' }}>
                                                        <div style={{ 
                                                            height: '100%', 
                                                            width: `${Math.min(100, (c.real_hours / c.budget_hours) * 100)}%`, 
                                                            background: c.isOverbudget ? '#ef4444' : '#10b981' 
                                                        }} />
                                                    </div>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', marginTop: '5px' }}>
                                                        <span style={{ color: c.isOverbudget ? '#ef4444' : '#333', fontWeight: 'bold' }}>{c.real_hours}h réelles</span>
                                                        <span style={{ color: '#64748b' }}>({c.approved_hours}h validées)</span>
                                                    </div>
                                                </div>
                                            )}

                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', fontWeight: 'bold', color: '#475569' }}>
                                                <span>👥 Effectif:</span>
                                                <div style={{ display: 'flex', gap: '5px' }}>
                                                    <span title="Planifié" style={{ background: '#f1f5f9', padding: '2px 5px', borderRadius: '4px' }}>{c.unique_workers_planned}P</span>
                                                    <span title="Intervenu" style={{ background: c.unique_workers_real > 0 ? '#dcfce7' : '#f1f5f9', color: c.unique_workers_real > 0 ? '#166534' : '#64748b', padding: '2px 5px', borderRadius: '4px' }}>{c.unique_workers_real}R</span>
                                                </div>
                                            </div>

                                            <a href="/admin/chantiers" style={{ display: 'block', textAlign: 'center', background: '#3b82f6', color: 'white', textDecoration: 'none', padding: '8px', borderRadius: '6px', marginTop: '15px', fontWeight: 'bold', fontSize: '0.85rem' }}>
                                                Gérer ce chantier
                                            </a>
                                        </div>
                                    </Popup>
                                </Marker>
                            ))}
                        </MarkerClusterGroup>
                    </MapContainer>
                )}
            </div>
        </div>
    );
};

export default AdminMap;
